import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './rule-editor.scss';
import { Link, useParams } from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import { SubMenus } from 'src/components/shared/sub-menus/sub-menus';
import { RuleTabs } from 'src/utils/enums';
import ArrowBack from '@svg/arrow-back.svg?react';
import { useStores } from 'src/components/store';
import { EditableRule } from 'src/model/EditableRule';
import RuleEditorContent from './rule-editor-content';
import { RuleName } from './rule-name/rule-name';
import { SaveRuleModal } from './save-rule-modal/save-rule-modal';
import { Button } from 'antd';
import useCustomModal from 'src/components/shared/custom-modal/custom-modal';
import ruleValidator from './rule-validator';
import { FieldMessage, getErrorsBySection, TabsErrors } from 'src/dtos/error-message.dto';
import ruleService from 'src/services/rule.service';
import { getUpdatedBy, MAX_CUSTOM_RULE_COUNT, useRuleConverter } from 'src/utils/ruleUtils';
import { buildUpdateRuleRequest } from 'src/utils/versionUtils';
import { Rule } from 'src/dtos/version.dto';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import CustomIcon, { IconType } from '../../../shared/custom-icon/custom-icon';
import ReactRouterPrompt from 'react-router-prompt';
import { QuitEditingModal } from 'src/components/shared/quit-editing-modal/quit-editing-modal';
import usePromiseFeedback from 'src/components/shared/promise-feedback/usePromiseFeedback';
import Loader from 'src/components/shared/loader/loader';

const RuleEditor: React.FC = observer(() => {

    const { fwVersionStore, customerStore } = useStores();
    const [showModal, , contextHolder] = useCustomModal();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isRuleChanged, setIsRuleChanged] = useState(false);
    const [originalRule, setOriginalRule] = useState<Rule | undefined>();
    const [editableRule, setEditableRule] = useState<EditableRule>();
    const [activeMenu, setActiveMenu] = useState<RuleTabs>(RuleTabs.GENERAL);
    const [shouldDisplayErrors, setShouldDisplayErrors] = useState<boolean>(false);
    const [displaySaveSummary, setDisplaySaveSummary] = useState<boolean>(false);
    const { convertRuleToEditable, generateNewRuleName, prepareRuleForSaving } = useRuleConverter();

    const { ruleId, versionId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        // the handler for actually showing the prompt
        const handler = (event: BeforeUnloadEvent) => {
            if (isRuleChanged) {
                event.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handler);
    }, []);

    useEffect(() => {
        if (isNotEditable || !ruleId && exceededMaxRulesCount) {
            goBack();
        }
        fwVersionStore.setFirewallRulesMode(ruleId ? 'edit' : 'new');
    }, []);

    useEffect(() => {
        if (!fwVersionStore.selectedFwVersion?.content.policy.rules) return;
        if (ruleId && ruleId !== editableRule?.id) {
            initializeExistingRule(ruleId);
        }
        else {
            if (fwVersionStore.customRulesCount >= MAX_CUSTOM_RULE_COUNT) {
                goBack();
            }
            if(!editableRule) {
                initializeNewRule();
            }
        }
    }, [ruleId, fwVersionStore.selectedFwVersion?.content.policy.rules]);

    useEffect(() => {
        if (displaySaveSummary) {
            showModal({
                wrapClassName: 'save-rule-summary',
                width: 600,
                title: isRuleValid ?
                    'Rule Saved Successfully' : 'Unable to Save Rule',
                body: () => <SaveRuleModal ruleErrors={errorsToDisplay} isRuleValid={isRuleValid} />,
                okText: 'Close',
                closable: true,
                onCancel: closeSummaryModal,
                onOk: closeSummaryModal
            });
        }
    }, [displaySaveSummary]);

    const ruleErrors: TabsErrors = useMemo(() => editableRule ?
        ruleValidator.generateErrorList(editableRule, fwVersionStore.selectedVersionRules) : {}, [editableRule, originalRule]);

    const isRuleValid: boolean = useMemo(() => !!ruleErrors &&
        Object.values(ruleErrors).every(section => section.every(field => field.isValid)), [ruleErrors]);

    const errorsToDisplay: TabsErrors = useMemo(() => shouldDisplayErrors ? ruleErrors : {}, [ruleErrors, shouldDisplayErrors]);

    const ruleNameErrors: { [field: string]: FieldMessage } = useMemo(() =>
            shouldDisplayErrors && !!ruleErrors ? getErrorsBySection(ruleErrors, RuleTabs.GENERAL) :
                {},
        [shouldDisplayErrors, ruleErrors]);

    const isReplacingToDraft = useMemo(() => {
        return fwVersionStore.selectedFwVersion?.externalId !== versionId;
    }, [fwVersionStore.selectedFwVersion, versionId]);

    const isNotEditable = fwVersionStore.selectedFwVersion && !fwVersionStore.isSelectedVersionEditable;

    const exceededMaxRulesCount = fwVersionStore.customRulesCount >= MAX_CUSTOM_RULE_COUNT;

    const goBack = () => {
        navigate(`../../../${fwVersionStore.selectedFwVersion?.externalId}/firewall-rules`);
    };

    const closeSummaryModal = () => {
        if (isRuleValid) {
            goBack();
        } else {
            setDisplaySaveSummary(false);
        }
    };

    const handleChange = useCallback((name: any, value: any) => {
        if (!editableRule) return;

        setIsRuleChanged(true);
        // we use with prevState to handle multiple updates in sequence 
        setEditableRule(prevState => {
            if (!prevState) return undefined;
            return {
                ...prevState,
                [name]: value,
            };
        });
    }, [editableRule]);

    const handleRuleChange = useCallback((updatedRule: Partial<EditableRule>) => {
        if (!editableRule) return;

        setIsRuleChanged(true);
        const updatedRuleData = {
            ...editableRule,
            ...updatedRule,
        };
        setEditableRule(updatedRuleData);
    }, [editableRule]);

    const saveRule = (saveMethod: any, args: any[]) => {
        promiseFeedbackFactory({
            promise: fwVersionStore.genericCrudMethod(saveMethod, args),
            successCallback: () => {
                setIsRuleChanged(false);
                setDisplaySaveSummary(true);
            },
            notifObj: {show: false}
        });
    };

    const initializeExistingRule = (ruleId: string) => {
        const rule = fwVersionStore.getRuleById(ruleId);
        if (!rule) return;
        setOriginalRule(rule);
        setEditableRule(convertRuleToEditable(rule));
    };

    const initializeNewRule = () => {
        const rule = new EditableRule();
        rule.position = fwVersionStore.newRulePosition;
        rule.name = generateNewRuleName();
        setEditableRule({ ...rule });
    };

    const validateAndSaveRule = () => {
        if(!editableRule) return;
        setShouldDisplayErrors(true);

        if (!isRuleValid) {
            setDisplaySaveSummary(true);
        }
        else {
            const rule = prepareRuleForSaving(editableRule, originalRule);
            performSave(rule);
        }
    };

    const performSave = (rule: Partial<Rule>) => {
        if (editableRule?.id && originalRule) {
            saveRule(ruleService.updateRule, [buildUpdateRuleRequest(rule as Rule, originalRule)]);
        } else {
            saveRule(ruleService.createRule, [rule]);
        }
    };

    const formatDate = moment(originalRule?.updatedDate).format('MMM Do YYYY');

    const formatUpdatedBy = useMemo(() => {
        if (originalRule) {
            const updatedBy = getUpdatedBy(originalRule);
            if (!updatedBy.includes('@') || updatedBy === 'System') {
                return <><CustomIcon type={IconType.APPLY_ATT} size={12}/> AT&T</>;
            }

            return updatedBy;
        }

        return '';
    }, [originalRule]);

    return editableRule ? <div className={'rule-editor'}>
        {fwVersionStore.isLoading && <Loader type="full-screen" />}
        <div className={'rule-editor-header'}>
            <Link to={`../../../${fwVersionStore.selectedFwVersion?.externalId}`}><ArrowBack /></Link>
            <RuleName handleChange={handleChange}
                      ruleData={editableRule}
                      sectionErrors={ruleNameErrors}  />
            {originalRule && <span className={'last-updated'}>
                <b>Last Updated</b> {formatDate} By {formatUpdatedBy}
            </span>}
        </div>
        <div className={'rule-editor-content'}>
            <SubMenus subMenusEnum={RuleTabs} activeSubMenu={activeMenu} setActiveSubMenu={setActiveMenu} errors={errorsToDisplay} />
            <div className="rule-editor-tab-content">
                <RuleEditorContent
                    handleChange={handleChange}
                    handleRuleChange={handleRuleChange}
                    ruleData={editableRule}
                    ruleErrors={errorsToDisplay}
                    ruleCount={fwVersionStore.rulesCount}
                    activeMenu={activeMenu}
                    allowDns={customerStore.isAdvancedConnection}
                />
            </div>
        </div>

        <div className={'footer-btns'}>
            <Button className={'custom-btn secondary-btn large-btn'} onClick={goBack}>Cancel</Button>
            <Button className={'custom-btn primary-btn large-btn save-btn'} onClick={validateAndSaveRule}>Save</Button>
        </div>
        <ReactRouterPrompt when={isRuleChanged && !isReplacingToDraft} >
            {({ isActive, onConfirm: onDiscaredChnages, onCancel: onStayEdit }) =>
                <>
                    {isActive
                        && <QuitEditingModal onDiscaredChanges={onDiscaredChnages} onStayEdit={onStayEdit}/>}
                </>
            }
        </ReactRouterPrompt>
        {contextHolder}
        {promiseContextHolder}
    </div> : <></>;
});

export default RuleEditor;